// Avatar generators used to fill `imgSrc` props across the demo.
//
// People — deterministic portrait URL from pravatar.cc (CC0 stock photos,
// same seed → same face, CORS friendly, no auth).
// Companies + contacts — inline SVG data URI with 1-2 char initials on a
// branded gradient. No external dep, works offline, deterministic colours.

const SVG_PALETTE = [
  ["#7c3aed", "#a855f7"], // violet
  ["#0ea5e9", "#38bdf8"], // sky
  ["#14b8a6", "#2dd4bf"], // teal
  ["#f59e0b", "#fbbf24"], // amber
  ["#ef4444", "#f87171"], // red
  ["#10b981", "#34d399"], // emerald
  ["#6366f1", "#818cf8"], // indigo
  ["#ec4899", "#f472b6"], // pink
  ["#f97316", "#fb923c"], // orange
  ["#0891b2", "#22d3ee"], // cyan
  ["#84cc16", "#a3e635"], // lime
  ["#8b5cf6", "#a78bfa"], // purple
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function initials(name: string): string {
  const tokens = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[1][0]).toUpperCase();
}

// Generates a small (40x40) gradient SVG with white initials. Encoded as a
// data URI so the project's CompanyAvatarWithLabel renders it like any other
// image (no external fetch, no caching surprises).
export function makeInitialsAvatar(name: string): string {
  const [from, to] = SVG_PALETTE[hashString(name) % SVG_PALETTE.length];
  const text = initials(name);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="${from}"/>
        <stop offset="100%" stop-color="${to}"/>
      </linearGradient>
    </defs>
    <rect width="40" height="40" rx="8" fill="url(#g)"/>
    <text x="50%" y="50%" dy=".1em" text-anchor="middle" dominant-baseline="middle"
      font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif"
      font-weight="600" font-size="16" fill="#ffffff">${text}</text>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

// Deterministic portrait image from pravatar.cc. Seed is the email so each
// team member always gets the same face across re-renders.
export function makePortraitAvatar(seed: string): string {
  return `https://i.pravatar.cc/150?u=${encodeURIComponent(seed)}`;
}
